import React, { useMemo, useState } from 'react';
import { signUp } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import {
  FormSection,
  FormField,
  SectionHeader,
  SectionBody,
  SectionFooter,
  Button,
  Link,
  Input,
  InputLabel,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
} from './auth-ui';

// The aws-amplify-react base class supported federated sign-in, phone-number
// fields with dial codes, `usernameAttributes` and custom: attribute prefixes.
// None of that is configured here -- SignUpConfig sets hideAllDefaults with
// four plain fields -- so this renders exactly those fields and nothing else.
function CustomSignUp({ theme, changeState, onError, signUpConfig }) {
  const fields = useMemo(
    () =>
      [...signUpConfig.signUpFields].sort(
        (a, b) => a.displayOrder - b.displayOrder
      ),
    [signUpConfig]
  );
  const [values, setValues] = useState({});
  const [pending, setPending] = useState(false);

  const handleChange = (key) => (event) =>
    setValues((current) => ({ ...current, [key]: event.target.value }));

  const submit = async () => {
    const missing = fields
      .filter((field) => field.required && !values[field.key])
      .map((field) => field.label);
    if (missing.length > 0) {
      onError(
        `The following fields need to be filled out: ${missing.join(', ')}`
      );
      return;
    }

    setPending(true);
    const { username, password, ...userAttributes } = values;
    try {
      // v3 took (username, password, attributes) flattened into one object;
      // v6 nests everything but the credentials under `options`.
      await signUp({ username, password, options: { userAttributes } });
      changeState('confirmSignUp', { username });
    } catch (err) {
      onError(err);
    }
    setPending(false);
  };

  return (
    <FormSection theme={theme} data-test="sign-up-section">
      <SectionHeader theme={theme} data-test="sign-up-header-section">
        {I18n.get(signUpConfig.header)}
      </SectionHeader>
      <SectionBody theme={theme} data-test="sign-up-body-section">
        {fields.map((field, index) => (
          <FormField theme={theme} key={field.key}>
            <InputLabel theme={theme}>
              {I18n.get(field.label)}
              {field.required ? ' *' : ''}
            </InputLabel>
            <Input
              autoFocus={index === 0}
              placeholder={I18n.get(field.placeholder)}
              theme={theme}
              type={field.type}
              name={field.key}
              value={values[field.key] || ''}
              onChange={handleChange(field.key)}
              data-test="sign-up-non-phone-number-input"
              autoCapitalize="off"
              disabled={pending}
            />
          </FormField>
        ))}
      </SectionBody>
      <SectionFooter theme={theme} data-test="sign-up-footer-section">
        <SectionFooterPrimaryContent theme={theme}>
          <Button
            onClick={submit}
            theme={theme}
            disabled={pending}
            data-test="sign-up-create-account-button"
          >
            {I18n.get('Create Account')}
          </Button>
        </SectionFooterPrimaryContent>
        <SectionFooterSecondaryContent theme={theme}>
          {I18n.get('Have an account? ')}
          <Link
            theme={theme}
            onClick={() => changeState('signIn')}
            data-test="sign-up-sign-in-link"
          >
            {I18n.get('Sign in')}
          </Link>
        </SectionFooterSecondaryContent>
      </SectionFooter>
    </FormSection>
  );
}

export default CustomSignUp;
